import { z } from "zod";
import { answerSchema } from "./answers";
import { publicSlideSchema } from "./deck";

/**
 * A self-paced session, as a participant's phone sees it (ADR 0008).
 *
 * Plain HTTP rather than the broker: nothing here is broadcast, each phone moves on its
 * own, and a request at a time is all one person filling in a quiz ever makes. Every
 * call carries the participant's join code and rejoin token, which is the same
 * credential a live session's WebSocket presents.
 *
 * Note what is absent throughout: no score, no rank, no right answer. In this mode
 * only the presenter sees the standings, and a slide shown here must not give away
 * the answer to whoever is filling it in next.
 */

const uuid = z.string().uuid();

/**
 * Allowance for a slide's journey to the phone: a self-paced question's clock starts as
 * the server sends it, not when the person can see it. An answer inside the grace still
 * scores, as if it arrived on the deadline; nothing after it counts. The phone waits it
 * out before moving on, so it never asks for the next slide while this one still counts.
 */
export const PACED_GRACE_MS = 1500;

/** Where a participant is: on a slide, or done. */
export const pacedStateSchema = z
  .object({
    /** The server's clock now, so the phone can run a countdown without trusting its own. */
    serverTime: z.string().datetime(),
    /** Self-paced sessions may close by themselves; the phone says so up front. */
    closesAt: z.string().datetime().nullable(),
    /** Slides this person will see, in order, and how many they have finished. */
    total: z.number().int().nonnegative(),
    done: z.number().int().nonnegative(),
    current: z
      .object({
        slide: publicSlideSchema,
        /** 0-based position among the slides this person will see. */
        index: z.number().int().nonnegative(),
        /** When this person was first shown it, on the server's clock. Their timer starts here. */
        startedAt: z.string().datetime(),
        /** startedAt + the slide's time limit; null when untimed. */
        deadline: z.string().datetime().nullable(),
        /** May they move on without answering? Content slides and unscored questions. */
        skippable: z.boolean(),
      })
      .strict()
      .nullable(),
  })
  .strict();

export type PacedState = z.infer<typeof pacedStateSchema>;

export const pacedAnswerSchema = z.object({ slideId: uuid, answer: answerSchema }).strict();
export type PacedAnswerRequest = z.infer<typeof pacedAnswerSchema>;

/** Move on from a slide without answering it — see `skippable`. */
export const pacedSkipSchema = z.object({ slideId: uuid }).strict();
export type PacedSkipRequest = z.infer<typeof pacedSkipSchema>;
